import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import {
  UseDateTimeDurationEventFragment,
  UseDateTimeDurationProposalFragment,
} from './useDateTimeDuration.generated-types'
import { round } from 'lodash'

export const nullReturn = { durationFullDate: null, durationMedDate: null, durationRange: null }

interface DateTimeDuration {
  durationFullDate: string
  durationMedDate: string
  durationRange: string
}

export const useDateTimeDuration = (
  eventOrProposal: UseDateTimeDurationEventFragment | UseDateTimeDurationProposalFragment
): DateTimeDuration | null => {
  const { t } = useTranslation()

  if (eventOrProposal == null) return nullReturn

  const { fromDateTime, toDateTime } = eventOrProposal

  if (!fromDateTime || !toDateTime) return nullReturn

  const from = DateTime.fromISO(fromDateTime, { zone: 'utc' })
  const to = DateTime.fromISO(toDateTime, { zone: 'utc' })

  const fromFullDate = from.toLocaleString(DateTime.DATE_FULL)
  const toFullDate = to.toLocaleString(DateTime.DATE_FULL)
  const fromMedDate = from.toLocaleString(DateTime.DATE_MED)
  const toMedDate = to.toLocaleString(DateTime.DATE_MED)
  const fromTime = from.toLocaleString(DateTime.TIME_24_SIMPLE)
  const toTime = to.toLocaleString(DateTime.TIME_24_SIMPLE)

  const diff = to.diff(from, ['hours', 'days'])

  if (diff.days === 0) {
    const roundedHours = round(diff.hours, 1)

    return {
      durationFullDate: fromFullDate,
      durationMedDate: fromMedDate,
      durationRange: `${fromTime} - ${toTime} ( ${roundedHours} ${t('duration.hours', 'hours')} )`,
    }
  } else {
    const days = diff.hours > 0 ? diff.days + 1 : diff.days
    const roundedDays = round(days, 1)

    return {
      durationFullDate: `${fromFullDate} - ${toFullDate}`,
      durationMedDate: `${fromMedDate} - ${toMedDate}`,
      durationRange: `( ${roundedDays} ${t('duration.days', 'days')} )`,
    }
  }
}
